import { LogOut } from 'lucide-react';
import { SidebarItems } from './SideBarItems';
import { useSidebar } from 'context/SideBarContext';
import { useAuth } from 'context/AuthContext';
import React, { useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import UserProfileBadge from './UserProfileBadge';
import MenuItem from './MenuItem';

const Sidebar = () => {
  const { isSidebarOpen, closeSidebar } = useSidebar();
  const { auth } = useAuth();
  const { keycloak, initialized } = useKeycloak();
  const client =
    process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'api-service-client';
  const isNTSAUser =
    keycloak.tokenParsed?.resource_access?.[client]?.roles?.includes('ntsa');

  const canAccessItem = (title: string, roles: string[]) => {
    if (roles.includes('finance')) return true;
    if (roles.includes('ntsa')) return title === 'Fines';
    if (roles.includes('officer')) return title === 'Fines';
    if (roles.includes('case_checker')) return title === 'Fines';
    return title !== 'Finance';
  };

  const filteredSidebarItems = SidebarItems.filter((item) =>
    canAccessItem(item.title, auth),
  );

  const logout = useCallback(() => {
    keycloak?.logout({ redirectUri: `${window.location.origin}/auth/login` });
  }, [keycloak]);

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <>
      {/* Mobile overlay — click to close */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 z-30
          h-full w-64 bg-white shadow-lg
          flex flex-col overflow-hidden
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo / profile badge */}
        <UserProfileBadge />

        {/* Nav items */}
        <nav
          className={`flex-1 px-3 py-4 overflow-y-auto ${isNTSAUser ? 'sidebar' : ''}`}
        >
          <ul className="space-y-1">
            {filteredSidebarItems.map((item, index) => (
              <li key={index}>
                <MenuItem item={item} />
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout */}
        <div
          className={`border-t p-4 ${isNTSAUser ? 'border-t-black bg-[#edf4f1] courier' : ''}`}
        >
          <button
            onClick={logout}
            className="w-full flex items-center space-x-2 p-2 rounded-md hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
